import { useAuthContext } from '@/auth/useAuthContext';
import { AssessmentOutcomePanel } from '@/components/AssessmentOutcomePanel';
import { DraftRestorePrompt } from '@/components/DraftRestorePrompt';
import { parseSpeciesAllowed } from '@/components/RunPicker';
/**
 * BookingDetail — full-page booking group renderer.
 *
 * Two-column layout (left = booking details + per-pet; right = costs,
 * payments, communications, activity). Rich editable fields with inline
 * save. All monetary values use .num class; all IDs shown monospace.
 *
 * Data is passed in by BookingDetailPage (never re-fetched here except
 * for the supplementary payments / comms / activity panels which are
 * loaded on mount).
 */
import {
  ApiError,
  type BookingCommRow,
  type BookingGroupCustomer,
  type BookingGroupDetail,
  type BookingGroupEntry,
  type BookingGroupStay,
  type BookingPayment,
  cancelBooking,
  createVaccineOverride,
  createWaitlistEntry,
  deleteBookingGroup,
  getRuns,
  listBookingComms,
  listBookingPayments,
  moveBoardingStayRun,
  openAndPrintRunCard,
  patchBookingMetadata,
  patchBookingStayDates,
  restoreBookingGroup,
  sendGroupToKiosk,
  staffChargeBalance,
  staffChargeBookingManual,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import type { Run } from '@/lib/types';
import { pushUndo } from '@/lib/undoStore';
import { useDraft } from '@/lib/useDraft';
import { useRealtime } from '@/lib/useRealtime';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  ListPlus,
  Loader2,
  LogIn,
  LogOut,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Phone,
  Printer,
  Send,
  Shield,
  ShieldOff,
  Syringe,
  Trash2,
  User,
  XCircle,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BelongingsSection } from './BelongingsSection';
import { BookingFeedingSection } from './BookingFeedingSection';
import { BookingMessagesPanel } from './BookingMessagesPanel';
import { BookingTransportSection } from './BookingTransportSection';
import { CancelBookingModal, type CancelReason } from './CancelBookingModal';
import { CheckinWorkflowDrawer } from './CheckinWorkflowDrawer';
import { CheckoutWorkflowDrawer } from './CheckoutWorkflowDrawer';
import { PetHealthPanel } from './PetHealthPanel';
import { ReportCardsPanel } from './ReportCardsPanel';
import { SendPaymentLinkDrawer } from './SendPaymentLinkDrawer';
import { ShareLiveStayPanel } from './ShareLiveStayPanel';
import { StaffCardPaymentPanel } from './StaffCardPaymentPanel';
import { StatusPill } from './StatusPill';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BookingDetailProps {
  groupId: string;
  groupDetail: BookingGroupDetail | { groupId: string; bookings: unknown[] } | null;
  onClose: () => void;
  onDeleted: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isEnrichedEntry(entry: unknown): entry is BookingGroupEntry {
  if (!entry || typeof entry !== 'object') return false;
  const e = entry as Record<string, unknown>;
  return (
    typeof e.booking === 'object' &&
    e.booking !== null &&
    typeof e.pet === 'object' &&
    e.pet !== null
  );
}

/** Human-readable label for a comm trigger key. */
function triggerLabel(key: string): string {
  const MAP: Record<string, string> = {
    'booking.confirmation': 'Booking Confirmation',
    'booking.reminder': 'Booking Reminder',
    'booking.modified': 'Booking Modified',
    'booking.cancelled': 'Booking Cancelled',
    'booking.checkin_complete': 'Check-in Complete',
    'booking.checkout_reminder': 'Checkout Reminder',
    'booking.photo.posted': 'Photo Posted',
    'booking.message.received_by_customer': 'Message to Customer',
    'booking.message.received_by_staff': 'Message to Staff',
    'refund.completed': 'Refund Completed',
    'daycare.checkin_reminder': 'Daycare Check-in Reminder',
    'grooming.appointment_confirmation': 'Grooming Confirmation',
    'grooming.appointment_reminder': 'Grooming Reminder',
    'grooming.appointment_cancelled': 'Grooming Cancelled',
    'grooming.completed': 'Grooming Completed',
  };
  return MAP[key] ?? key.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const SECTION_LABEL = 'text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';
const CARD = 'rounded-xl border border-border bg-surface';
const SECTION_HEADER = 'flex items-center gap-2 px-5 py-3.5 border-b border-border';
/** 44px touch target below `md`; desktop keeps the compact padding-driven height. */
const TOUCH = 'min-h-11 md:min-h-0';
/**
 * Anchor targets for the mobile section chips. The AppShell mobile header is
 * 61px tall and the sticky chip row sits directly under it (~53px), so headings
 * need ~120px of scroll margin below `md`; on desktop neither bar exists.
 */
const SECTION_ANCHOR = 'scroll-mt-[120px] md:scroll-mt-4';
const MOBILE_CHIP_TOP = 'top-[61px]';

/** Which guided workflow the primary action opens for a booking status. */
function canCheckinStatus(status: string): boolean {
  return status === 'confirmed' || status === 'confirmed_deposit' || status === 'paid';
}
function canCheckoutStatus(status: string): boolean {
  return status === 'checked_in';
}

type WorkflowKind = 'checkin' | 'checkout';

/** Sections the mobile chip row can point at (only rendered ones get a chip). */
interface SectionChip {
  id: string;
  label: string;
}

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// ---------------------------------------------------------------------------
// EditableTextarea
// ---------------------------------------------------------------------------

interface EditableTextareaProps {
  label: string;
  value: string;
  placeholder?: string;
  onSave: (v: string) => void;
  /**
   * When set, in-progress edits autosave to localStorage under this key and
   * a "Restore unsaved draft?" prompt appears if a draft is found (e.g. the
   * tab froze mid-note). Cleared on Save.
   */
  draftKey?: string;
}

function EditableTextarea({ label, value, placeholder, onSave, draftKey }: EditableTextareaProps) {
  const [editing, setEditing] = useState(false);
  const autosave = useDraft<string>(draftKey ?? null, value);
  const draft = autosave.value;
  const setDraft = autosave.setValue;
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing, setDraft]);

  function open() {
    setDraft(value);
    setEditing(true);
    setTimeout(() => ref.current?.focus(), 0);
  }

  function save() {
    setEditing(false);
    if (draft !== value) onSave(draft);
    autosave.clearDraft();
  }

  return (
    <div>
      <p className={SECTION_LABEL}>{label}</p>
      {autosave.pendingDraft !== null && !editing && (
        <div className="mt-1">
          <DraftRestorePrompt
            onRestore={() => {
              autosave.restoreDraft();
              setEditing(true);
              setTimeout(() => ref.current?.focus(), 0);
            }}
            onDiscard={autosave.discardDraft}
          />
        </div>
      )}
      {editing ? (
        <div className="mt-0.5">
          <textarea
            ref={ref}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="min-h-11 w-full rounded-md border border-blue-400 bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={save}
              className={`${TOUCH} rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700`}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(value);
                setEditing(false);
              }}
              className={`${TOUCH} rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary`}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={open}
          className="group mt-0.5 flex w-full items-start gap-1.5 text-left text-sm text-foreground"
        >
          <span className={value ? '' : 'italic text-muted-foreground'}>
            {value || (placeholder ?? '—')}
          </span>
          <Pencil
            className="mt-0.5 size-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-70"
            strokeWidth={1.75}
          />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaymentsPanel
// ---------------------------------------------------------------------------

interface PaymentsPanelProps {
  bookingId: string;
  totalMinor: number;
  currency: string;
  /** Contact details for the "Send payment link" drawer (migration 0098). */
  customer?: { firstName: string; email?: string | null; phone?: string | null } | null;
  /**
   * Reports the outstanding balance whenever it changes so the parent can
   * decide whether "Take payment" is the next action (mobile bottom bar).
   */
  onOutstandingChange?: (outstandingMinor: number) => void;
}

function PaymentsPanel({
  bookingId,
  totalMinor,
  currency,
  customer,
  onOutstandingChange,
}: PaymentsPanelProps) {
  const [payments, setPayments] = useState<BookingPayment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeSource, setChargeSource] = useState<'cash' | 'check' | 'manual'>('cash');
  const [charging, setCharging] = useState(false);
  const [chargeError, setChargeError] = useState<string | null>(null);
  // Charge card on file
  const [chargingCard, setChargingCard] = useState(false);
  const [chargeCardError, setChargeCardError] = useState<string | null>(null);
  // Take card payment via Stripe.js
  const [showCardPanel, setShowCardPanel] = useState(false);
  // Text-to-pay link (migration 0098)
  const [payLinkOpen, setPayLinkOpen] = useState(false);

  function reloadPayments() {
    setLoading(true);
    listBookingPayments(bookingId)
      .then((r) => setPayments(r.payments))
      .catch(() => setPayments([]))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reloadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  async function handleCharge() {
    const amountMinor = Math.round(Number.parseFloat(chargeAmount) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      setChargeError('Enter a valid amount');
      return;
    }
    setCharging(true);
    setChargeError(null);
    try {
      await staffChargeBookingManual(bookingId, { amountMinor, source: chargeSource });
      setChargeOpen(false);
      setChargeAmount('');
      reloadPayments();
    } catch (e: unknown) {
      setChargeError((e as Error).message ?? 'Charge failed');
    } finally {
      setCharging(false);
    }
  }

  async function handleChargeCard() {
    setChargingCard(true);
    setChargeCardError(null);
    try {
      await staffChargeBalance(bookingId);
      reloadPayments();
    } catch (e: unknown) {
      const code = e instanceof ApiError ? e.code : null;
      const friendly =
        code === 'no_default_pm'
          ? 'No saved card on file — take payment another way.'
          : code === 'connect_off' || code === 'stripe_off'
            ? 'Stripe is not configured for this account. Take payment another way.'
            : code === 'pi_failed'
              ? "Card charge failed. Check the customer's card details or take payment another way."
              : ((e as Error).message ?? 'Charge failed');
      setChargeCardError(friendly);
    } finally {
      setChargingCard(false);
    }
  }

  // Gratuities (kind='tip') never count towards the bill (migration 0098).
  const paid = payments
    ? payments
        .filter((p) => p.direction === 'in' && p.status === 'succeeded' && p.kind !== 'tip')
        .reduce((acc, p) => acc + p.amountMinor, 0)
    : 0;

  const outstanding = Math.max(0, totalMinor - paid);

  // Only meaningful once the ledger has loaded — a pre-load "outstanding =
  // total" would flash "Take payment" on fully paid bookings.
  useEffect(() => {
    if (!loading && payments) onOutstandingChange?.(outstanding);
  }, [loading, payments, outstanding, onOutstandingChange]);

  const sourceLabel = (source: string) => {
    const MAP: Record<string, string> = {
      stripe_card: 'Card',
      stripe_terminal: 'Terminal',
      cash: 'Cash',
      check: 'Cheque',
      account_credit: 'Credit',
      manual: 'Manual',
    };
    return MAP[source] ?? source;
  };

  function handlePrintReceipt() {
    const paymentRows = (payments ?? [])
      .filter((p) => p.direction === 'in' && p.status === 'succeeded')
      .map(
        (p) =>
          `  ${new Date(p.createdAt).toLocaleDateString('en-GB')} — ${sourceLabel(p.source)}: ${formatPriceMinor(p.amountMinor, currency)}`,
      )
      .join('\n');

    const content = [
      `Booking: #${bookingId.slice(-8).toUpperCase()}`,
      `Total: ${formatPriceMinor(totalMinor, currency)}`,
      `Paid: ${formatPriceMinor(paid, currency)}`,
      outstanding > 0 ? `Outstanding: ${formatPriceMinor(outstanding, currency)}` : 'Fully paid',
      '',
      'Payments:',
      paymentRows,
    ].join('\n');

    const escaped = content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = `<!DOCTYPE html><html><head><title>Receipt</title><style>body{font-family:monospace;padding:2rem;font-size:14px;line-height:1.7}pre{white-space:pre-wrap}</style></head><body><pre>${escaped}</pre></body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win)
      win.addEventListener('load', () => {
        win.print();
        URL.revokeObjectURL(url);
      });
    else URL.revokeObjectURL(url);
  }

  return (
    <div id="section-payments" className={`${CARD} ${SECTION_ANCHOR}`}>
      <div className={SECTION_HEADER}>
        <CreditCard className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="font-heading text-sm font-semibold text-foreground">Payments</h3>
        {payments && payments.length > 0 && (
          <button
            type="button"
            onClick={handlePrintReceipt}
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title="Print receipt"
          >
            <Printer className="size-3.5" strokeWidth={1.75} />
            Receipt
          </button>
        )}
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3 border-b border-border px-5 py-3">
        <div className="min-w-0">
          <p className={SECTION_LABEL}>Total</p>
          <p className="num text-sm font-semibold">{formatPriceMinor(totalMinor, currency)}</p>
        </div>
        <div className="min-w-0">
          <p className={SECTION_LABEL}>Paid</p>
          <p className="num text-sm font-semibold text-emerald-700">
            {formatPriceMinor(paid, currency)}
          </p>
        </div>
        <div className="min-w-0">
          <p className={SECTION_LABEL}>Outstanding</p>
          {outstanding <= 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-200">
              <CheckCircle className="size-3" strokeWidth={2} />
              Fully paid
            </span>
          ) : (
            <p className="num text-sm font-semibold text-destructive">
              {formatPriceMinor(outstanding, currency)}
            </p>
          )}
        </div>
      </div>

      {/* Payment rows */}
      <div className="px-5 py-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
            Loading…
          </div>
        ) : !payments || payments.length === 0 ? (
          <p className="text-xs text-muted-foreground">No payments recorded yet.</p>
        ) : (
          <div className="divide-y divide-border/50">
            {payments.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-2"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                    {sourceLabel(p.source)}
                  </span>
                  <span className="num text-xs text-muted-foreground">
                    {formatDate(p.createdAt)}
                  </span>
                  <span className="text-xs capitalize text-muted-foreground">{p.kind}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`num text-sm font-medium ${p.direction === 'out' ? 'text-destructive' : 'text-foreground'}`}
                  >
                    {p.direction === 'out' ? '−' : ''}
                    {formatPriceMinor(p.amountMinor, currency)}
                  </span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                      p.status === 'succeeded'
                        ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                        : p.status === 'failed'
                          ? 'bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20'
                          : 'bg-secondary text-muted-foreground'
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment actions */}
      <div className="border-t border-border px-5 py-3 space-y-3">
        {/* Charge card on file */}
        {!showCardPanel && (
          <div>
            {chargeCardError && (
              <p className="mb-1.5 text-xs text-destructive">{chargeCardError}</p>
            )}
            <button
              type="button"
              disabled={chargingCard}
              onClick={() => void handleChargeCard()}
              className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-60`}
            >
              {chargingCard ? (
                <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
              ) : (
                <Zap className="size-3.5" strokeWidth={1.75} />
              )}
              Charge card on file
            </button>
          </div>
        )}

        {/* Take card payment (Stripe.js) */}
        {!showCardPanel && (
          <button
            type="button"
            onClick={() => setShowCardPanel(true)}
            className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100`}
          >
            <CreditCard className="size-3.5" strokeWidth={1.75} />
            Take card payment
          </button>
        )}

        {/* Inline Stripe.js card payment panel */}
        {showCardPanel && (
          <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-3">
            <StaffCardPaymentPanel
              bookingId={bookingId}
              currency={currency}
              onSuccess={() => {
                setShowCardPanel(false);
                reloadPayments();
              }}
              onCancel={() => setShowCardPanel(false)}
            />
          </div>
        )}

        {/* Text-to-pay link (migration 0098) */}
        {!chargeOpen && !showCardPanel && (
          <button
            type="button"
            onClick={() => setPayLinkOpen(true)}
            className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary`}
            data-testid="send-payment-link-btn"
          >
            <Send className="size-3.5" strokeWidth={1.75} />
            Send payment link
          </button>
        )}
        <SendPaymentLinkDrawer
          open={payLinkOpen}
          onClose={() => setPayLinkOpen(false)}
          target={{ purpose: 'booking_balance', bookingId }}
          defaultAmountMinor={outstanding > 0 ? outstanding : null}
          currency={currency}
          customerFirstName={customer?.firstName ?? 'The customer'}
          hasEmail={customer ? Boolean(customer.email) : true}
          hasPhone={customer ? Boolean(customer.phone) : true}
        />

        {/* Record manual payment (cash / cheque / card recorded elsewhere) */}
        {!chargeOpen && !showCardPanel && (
          <button
            type="button"
            onClick={() => setChargeOpen(true)}
            className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary`}
          >
            <DollarSign className="size-3.5" strokeWidth={1.75} />
            Record manual payment
          </button>
        )}
        {chargeOpen && (
          <div className="space-y-2.5">
            <p className={SECTION_LABEL}>Record manual payment</p>
            {chargeError && <p className="text-xs text-destructive">{chargeError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative min-w-0 flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  {currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'}
                </span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={chargeAmount}
                  onChange={(e) => setChargeAmount(e.target.value)}
                  placeholder="0.00"
                  className="num h-11 w-full rounded-md border border-border bg-background pl-6 pr-2 text-sm outline-none focus:ring-2 focus:ring-ring/30 md:h-8"
                />
              </div>
              <select
                value={chargeSource}
                onChange={(e) => setChargeSource(e.target.value as 'cash' | 'check' | 'manual')}
                className="h-11 rounded-md border border-border bg-background px-2 text-xs outline-none md:h-8"
              >
                <option value="cash">Cash</option>
                <option value="check">Cheque</option>
                <option value="manual">Card (recorded elsewhere)</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={charging}
                onClick={() => void handleCharge()}
                className={`${TOUCH} flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60`}
              >
                {charging && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
                Record payment
              </button>
              <button
                type="button"
                onClick={() => {
                  setChargeOpen(false);
                  setChargeError(null);
                  setChargeAmount('');
                }}
                className={`${TOUCH} rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CommsPanel
// ---------------------------------------------------------------------------

function CommsPanel({ bookingId }: { bookingId: string }) {
  const [comms, setComms] = useState<BookingCommRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listBookingComms(bookingId)
      .then((r) => setComms(r.comms))
      .catch(() => setComms([]))
      .finally(() => setLoading(false));
  }, [bookingId]);

  const statusColor = (status: string) => {
    if (status === 'delivered')
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200';
    if (status === 'failed_retryable' || status === 'bounced')
      return 'bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20';
    return 'bg-secondary text-muted-foreground';
  };

  const channelIcon = (channel: string) => {
    if (channel === 'email')
      return <Mail className="inline size-3" strokeWidth={1.75} aria-label="Email" />;
    if (channel === 'sms')
      return <MessageSquare className="inline size-3" strokeWidth={1.75} aria-label="SMS" />;
    if (channel === 'whatsapp')
      return <Phone className="inline size-3" strokeWidth={1.75} aria-label="WhatsApp" />;
    return channel;
  };

  return (
    <div id="section-comms" className={`${CARD} ${SECTION_ANCHOR}`}>
      <div className={SECTION_HEADER}>
        <MessageSquare className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="font-heading text-sm font-semibold text-foreground">Communications</h3>
      </div>
      <div className="px-5 py-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
            Loading…
          </div>
        ) : !comms || comms.length === 0 ? (
          <p className="text-xs text-muted-foreground">No messages sent for this booking yet.</p>
        ) : (
          <div className="divide-y divide-border/50">
            {comms.map((c) => (
              <div key={c.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">
                    {triggerLabel(c.triggerKey)}
                  </p>
                  <p className="num text-[10.5px] text-muted-foreground">
                    {channelIcon(c.channel)} ·{' '}
                    {c.sentAt ? formatDateTime(c.sentAt) : `Queued ${formatDateTime(c.enqueuedAt)}`}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase ${statusColor(c.status)}`}
                >
                  {c.status.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityPanel
// ---------------------------------------------------------------------------

function ActivityPanel({ bookingId }: { bookingId: string }) {
  // Activity is fetched via the booking's subject in the activity_log.
  // We query GET /api/audit-log?subjectId=:id&subjectType=booking from the
  // staff audit API (sub-spec 11 Plan 3).
  type ActivityRow = { id: string; action: string; createdAt: string; metadata: string | null };
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(
      `/api/audit-log?subjectId=${encodeURIComponent(bookingId)}&subjectType=booking&limit=25`,
      {
        credentials: 'include',
      },
    )
      .then((r) => (r.ok ? r.json() : Promise.resolve({ entries: [] })))
      .then((d: { entries?: ActivityRow[] }) => setRows(d.entries ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [bookingId]);

  function actionLabel(action: string): string {
    return action.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return (
    <div id="section-activity" className={`${CARD} ${SECTION_ANCHOR}`}>
      <div className={SECTION_HEADER}>
        <Activity className="size-4 text-muted-foreground" strokeWidth={1.75} />
        <h3 className="font-heading text-sm font-semibold text-foreground">Activity</h3>
      </div>
      <div className="px-5 py-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
            Loading…
          </div>
        ) : !rows || rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <div className="divide-y divide-border/50">
            {rows.map((r) => (
              <div key={r.id} className="flex items-start gap-2 py-2">
                <div className="mt-0.5 size-1.5 shrink-0 rounded-full bg-muted-foreground/40" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground">{actionLabel(r.action)}</p>
                  <p className="num text-[10.5px] text-muted-foreground">
                    {formatDateTime(r.createdAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-pet card (left column)
// ---------------------------------------------------------------------------

interface PetSectionProps {
  entry: BookingGroupEntry;
  runs: Run[];
  savingStayId: string | null;
  busy: string | null;
  customer: BookingGroupCustomer | null;
  currency: string;
  totalMinor: number;
  onCancel: (id: string) => void;
  /**
   * Fired by the guided drawers AFTER the API call succeeded. The drawer owns
   * the POST — the parent only closes and refreshes. (Previously this re-ran
   * `staffCheckoutBooking()` a second time, which 409'd on the already
   * checked-out booking and surfaced spurious errors after a real success.)
   */
  onWorkflowDone: () => void;
  onStaySave: (
    bookingId: string,
    patch: {
      checkIn: string;
      checkout: string;
      feeding?: string | null;
      medication?: string | null;
    },
  ) => Promise<void>;
  onRunChange: (bookingId: string, runId: string | null) => Promise<void>;
  /**
   * Which guided workflow drawer is open for this booking. Lifted to the
   * parent so the mobile bottom bar can open the same drawer the in-card
   * buttons do.
   */
  openWorkflow: WorkflowKind | null;
  onOpenWorkflow: (kind: WorkflowKind | null) => void;
  /**
   * True for the first pet only — it carries the `section-*` ids the mobile
   * chip row anchors to (ids must be unique per page).
   */
  anchors?: boolean;
}

function PetSection({
  entry,
  runs,
  savingStayId,
  busy,
  customer,
  currency,
  totalMinor,
  onCancel,
  onWorkflowDone,
  onStaySave,
  onRunChange,
  openWorkflow,
  onOpenWorkflow,
  anchors = false,
}: PetSectionProps) {
  const anchorId = (name: string) => (anchors ? `section-${name}` : undefined);
  const anchorClass = anchors ? SECTION_ANCHOR : '';
  const noun = useSpaceNoun();
  const { booking, stay, groomingStartAt, groomingDurationMin, lineItems, intakeResponses, pet } =
    entry;
  const bookingId = booking.id;
  const status = booking.status;
  const type = booking.type;

  // Local editable stay state.
  const [localStay, setLocalStay] = useState<BookingGroupStay | null>(stay);

  // Guided workflow drawer state (controlled by the parent).
  const checkinDrawerOpen = openWorkflow === 'checkin';
  const checkoutDrawerOpen = openWorkflow === 'checkout';

  // Realtime messages/photos — bump a nonce so MessagesThread re-fetches.
  const [messagesNonce, setMessagesNonce] = useState(0);
  const bumpNonce = useCallback(() => setMessagesNonce((n) => n + 1), []);
  useRealtime((event) => {
    if (
      (event.type === 'BOOKING_MESSAGE_POSTED' || event.type === 'BOOKING_PHOTO_POSTED') &&
      event.booking_id === bookingId
    ) {
      bumpNonce();
    }
  });

  // Capability: messages.moderate — lets authorised staff delete messages.
  const { state: authState } = useAuthContext();
  const canModerate = authState.me?.capabilities?.includes('messages.moderate') ?? false;

  // Sync when parent data changes.
  useEffect(() => {
    setLocalStay(stay);
  }, [stay]);

  // Vaccine override modal state
  const [overrideTarget, setOverrideTarget] = useState<{
    vaccineId: string;
    vaccineName: string;
  } | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideValidUntil, setOverrideValidUntil] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [overrideGranted, setOverrideGranted] = useState<Set<string>>(new Set());

  async function handleGrantOverride() {
    if (!overrideTarget || !overrideReason.trim() || !pet.id) return;
    setOverriding(true);
    setOverrideError(null);
    try {
      await createVaccineOverride({
        petId: pet.id,
        vaccineId: overrideTarget.vaccineId,
        reason: overrideReason.trim(),
        validUntil: overrideValidUntil || null,
      });
      setOverrideGranted((prev) => new Set([...prev, overrideTarget.vaccineId]));
      setOverrideTarget(null);
      setOverrideReason('');
      setOverrideValidUntil('');
    } catch (e: unknown) {
      setOverrideError((e as Error).message ?? 'Failed to grant override');
    } finally {
      setOverriding(false);
    }
  }

  const canCancel = status !== 'cancelled' && status !== 'checked_out' && status !== 'no_show';
  const canCheckin = canCheckinStatus(status);
  const canCheckout = canCheckoutStatus(status);

  // Warn (but don't block) check-in if any vaccine is invalid and not overridden.
  const hasInvalidVaccines =
    canCheckin &&
    Array.isArray(pet.vaccinations) &&
    pet.vaccinations.some((v) => !v.valid && !overrideGranted.has(v.vaccineId));

  // Format ISO datetime-local value.
  function toDatetimeLocal(iso: string): string {
    // Truncate to minutes for datetime-local input.
    return iso.substring(0, 16);
  }

  return (
    <div id={anchorId('pets')} className={`${CARD} ${anchorClass}`}>
      {/* Pet header — stacks on phones so the action row wraps under the name
          instead of forcing the card wider than the viewport. */}
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-xs font-semibold uppercase text-muted-foreground"
            aria-label={pet.species}
          >
            {pet.species === 'cat' ? 'CAT' : 'DOG'}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-semibold text-foreground">{pet.name}</p>
              <StatusPill status={status} />
            </div>
            {pet.breed && <p className="text-xs text-muted-foreground">{pet.breed}</p>}
            {/* Always-visible edit shortcuts */}
            <div className="mt-1 flex flex-wrap items-center gap-3">
              {pet.id && customer?.id && (
                <Link
                  to={`/customers/${customer.id}/pets/${pet.id}/edit`}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                >
                  <Syringe className="size-3" strokeWidth={1.75} />
                  Edit vaccines
                </Link>
              )}
              {customer?.id && (
                <Link
                  to={`/customers/${customer.id}/edit`}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                >
                  <User className="size-3" strokeWidth={1.75} />
                  Edit customer
                </Link>
              )}
              {pet.id && (
                <Link
                  to={`/incidents/new?petId=${pet.id}&petName=${encodeURIComponent(pet.name)}&bookingId=${bookingId}`}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                >
                  <AlertTriangle className="size-3" strokeWidth={1.75} />
                  Report incident
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-start gap-2 sm:justify-end">
          {canCheckin && (
            <div className="flex flex-col items-start gap-1.5 sm:items-end">
              {hasInvalidVaccines && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                  <AlertTriangle className="size-3" strokeWidth={2} />
                  Vaccine issue
                </span>
              )}
              <button
                type="button"
                disabled={!!busy}
                onClick={() => onOpenWorkflow('checkin')}
                className={`${TOUCH} flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-60 ${
                  hasInvalidVaccines
                    ? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                }`}
              >
                <LogIn className="size-3.5" strokeWidth={1.75} />
                Check in
              </button>
            </div>
          )}
          {canCheckout && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() => onOpenWorkflow('checkout')}
              className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-60`}
            >
              <LogOut className="size-3.5" strokeWidth={1.75} />
              Check out
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              disabled={!!busy}
              onClick={() => onCancel(bookingId)}
              className={`${TOUCH} flex items-center gap-1.5 rounded-md border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60`}
            >
              {busy === `cancel-${bookingId}` ? (
                <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
              ) : (
                <XCircle className="size-3.5" strokeWidth={1.75} />
              )}
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Stay dates (boarding only — editable) */}
      {type === 'boarding' && stay && (
        <div id={anchorId('stay')} className={`border-t border-border px-5 py-3 ${anchorClass}`}>
          <p className={`${SECTION_LABEL} mb-2.5 flex items-center gap-1.5`}>
            <Calendar className="size-3.5" strokeWidth={1.75} />
            Stay Dates
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className={SECTION_LABEL}>Check-in</p>
              <input
                type="datetime-local"
                defaultValue={toDatetimeLocal(localStay?.checkIn ?? stay.checkIn)}
                className="num mt-0.5 min-h-11 w-full min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 outline-none md:min-h-0"
                id={`checkin-${bookingId}`}
              />
            </div>
            <div>
              <p className={SECTION_LABEL}>Checkout</p>
              <input
                type="datetime-local"
                defaultValue={toDatetimeLocal(localStay?.checkout ?? stay.checkout)}
                className="num mt-0.5 min-h-11 w-full min-w-0 rounded-md border border-border bg-background px-2 py-1 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 outline-none md:min-h-0"
                id={`checkout-${bookingId}`}
              />
            </div>
          </div>

          {/* Run assignment */}
          <div className="mt-3">
            <p className={SECTION_LABEL}>{noun.Singular}</p>
            <select
              defaultValue={localStay?.runId ?? ''}
              data-testid={`run-select-${bookingId}`}
              onChange={async (e) => {
                const val = e.target.value || null;
                await onRunChange(bookingId, val);
              }}
              className="mt-0.5 min-h-11 w-full rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 md:min-h-0"
            >
              <option value="">Unassigned</option>
              {runs
                .filter(
                  (r) =>
                    r.isActive === 1 &&
                    // Only spaces that take this pet's species — plus the one
                    // it is already in, so the current value still renders.
                    (r.id === localStay?.runId ||
                      parseSpeciesAllowed(r.speciesAllowed).includes(pet.species)),
                )
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </select>
            {runs.some((r) => r.isActive === 1) &&
              !runs.some(
                (r) =>
                  r.isActive === 1 && parseSpeciesAllowed(r.speciesAllowed).includes(pet.species),
              ) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  No {noun.plural} at this site take {pet.species}s.
                </p>
              )}
          </div>

          {/* Feeding & medication */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className={SECTION_LABEL}>Feeding instructions</p>
              <textarea
                defaultValue={
                  ((entry as unknown as Record<string, unknown>)?.feeding as string) ?? ''
                }
                rows={2}
                placeholder="e.g. 200g kibble twice daily"
                className="mt-0.5 min-h-11 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                id={`feeding-${bookingId}`}
              />
            </div>
            <div>
              <p className={SECTION_LABEL}>Medication notes</p>
              <textarea
                defaultValue={
                  ((entry as unknown as Record<string, unknown>)?.medication as string) ?? ''
                }
                rows={2}
                placeholder="e.g. 1 tablet at 08:00"
                className="mt-0.5 min-h-11 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                id={`medication-${bookingId}`}
              />
            </div>
          </div>

          <button
            type="button"
            disabled={savingStayId === bookingId}
            onClick={async () => {
              const checkinEl = document.getElementById(
                `checkin-${bookingId}`,
              ) as HTMLInputElement | null;
              const checkoutEl = document.getElementById(
                `checkout-${bookingId}`,
              ) as HTMLInputElement | null;
              const feedingEl = document.getElementById(
                `feeding-${bookingId}`,
              ) as HTMLTextAreaElement | null;
              const medicationEl = document.getElementById(
                `medication-${bookingId}`,
              ) as HTMLTextAreaElement | null;
              await onStaySave(bookingId, {
                checkIn: checkinEl?.value ? `${checkinEl.value}:00` : stay.checkIn,
                checkout: checkoutEl?.value ? `${checkoutEl.value}:00` : stay.checkout,
                feeding: feedingEl?.value ?? null,
                medication: medicationEl?.value ?? null,
              });
            }}
            className={`${TOUCH} mt-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60`}
          >
            {savingStayId === bookingId ? (
              <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
            ) : null}
            Save stay details
          </button>
        </div>
      )}

      {/* Grooming appointment time */}
      {!stay && type === 'grooming' && (
        <div className="border-t border-border px-5 py-3">
          <p className={`${SECTION_LABEL} mb-1 flex items-center gap-1.5`}>
            <Clock className="size-3.5" strokeWidth={1.75} />
            Appointment
          </p>
          <p className="num text-sm font-medium">
            {groomingStartAt ? formatDateTime(groomingStartAt) : formatDateTime(booking.createdAt)}
            {groomingDurationMin != null && (
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                ({groomingDurationMin} min)
              </span>
            )}
          </p>
        </div>
      )}

      {/* Evaluation day (migration 0099) — renders nothing for other service bookings */}
      {!stay && type === 'service' && <AssessmentOutcomePanel bookingId={bookingId} />}

      {/* Vaccinations */}
      {pet.vaccinations && pet.vaccinations.length > 0 && (
        <div className="border-t border-border px-5 py-3">
          <p className={`${SECTION_LABEL} mb-2 flex items-center gap-1.5`}>
            <Syringe className="size-3.5" strokeWidth={1.75} />
            Vaccinations
          </p>
          <div className="divide-y divide-border/40">
            {pet.vaccinations.map((v) => {
              const granted = overrideGranted.has(v.vaccineId);
              const effectivelyValid = v.valid || granted;
              return (
                <div
                  key={v.id ?? v.vaccineId}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {effectivelyValid ? (
                      <Shield className="size-3.5 text-emerald-600" strokeWidth={2} />
                    ) : (
                      <ShieldOff className="size-3.5 text-destructive" strokeWidth={2} />
                    )}
                    <span className="text-xs font-medium">{v.vaccineName ?? v.vaccineId}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {v.expiresOn && (
                      <span className="num text-[10.5px] text-muted-foreground">
                        Exp{' '}
                        {new Date(v.expiresOn).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                    )}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        effectivelyValid
                          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                          : 'bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20'
                      }`}
                    >
                      {granted ? 'Override' : effectivelyValid ? 'Valid' : 'Expired'}
                    </span>
                    {!effectivelyValid && !granted && (
                      <button
                        type="button"
                        onClick={() =>
                          setOverrideTarget({
                            vaccineId: v.vaccineId,
                            vaccineName: v.vaccineName ?? v.vaccineId,
                          })
                        }
                        className="rounded px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 hover:bg-amber-100"
                      >
                        Grant override
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Grant override modal */}
          {overrideTarget && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-xs font-semibold text-amber-900">
                Grant override — {overrideTarget.vaccineName}
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Reason (required)"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="w-full rounded border border-border bg-white px-2 py-1 text-xs"
                />
                <div>
                  <label className="mb-0.5 block text-[10px] text-muted-foreground">
                    Valid until (optional)
                  </label>
                  <input
                    type="date"
                    value={overrideValidUntil}
                    onChange={(e) => setOverrideValidUntil(e.target.value)}
                    className="w-full rounded border border-border bg-white px-2 py-1 text-xs"
                  />
                </div>
                {overrideError && <p className="text-xs text-destructive">{overrideError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={overriding || !overrideReason.trim()}
                    onClick={() => void handleGrantOverride()}
                    className="rounded bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {overriding ? 'Granting…' : 'Grant'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOverrideTarget(null);
                      setOverrideReason('');
                      setOverrideValidUntil('');
                      setOverrideError(null);
                    }}
                    className="rounded border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Health monitoring panel */}
      {pet.id && (
        <div className="border-t border-border px-5 py-3">
          <PetHealthPanel
            petId={pet.id}
            petName={pet.name}
            petSpecies={pet.species ?? 'dog'}
            bookingId={bookingId}
            canWrite
          />
        </div>
      )}

      {/* Line items */}
      {lineItems.length > 0 && (
        <div className="border-t border-border px-5 py-3">
          <p className={`${SECTION_LABEL} mb-2`}>Services</p>
          <div className="divide-y divide-border/50">
            {lineItems.map((li) => (
              <div key={li.id} className="flex items-center justify-between py-1.5 text-sm">
                {li.kind === 'overstay_night' ? (
                  // Migration 0103 — extra night added by the overstay policy.
                  <span className="num" data-testid="overstay-line-item">
                    Extra night
                    {li.scheduledDate ? ` — ${formatDate(li.scheduledDate)}` : ''}
                    <span className="ml-1.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-medium uppercase tracking-[0.1em] text-amber-800 ring-1 ring-amber-200">
                      Overstay
                    </span>
                  </span>
                ) : (
                  <span>{li.serviceName ?? li.serviceId}</span>
                )}
                <span className="num text-muted-foreground">{formatPriceMinor(li.priceMinor)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Intake responses */}
      {intakeResponses.length > 0 && (
        <div className="border-t border-border px-5 py-3">
          <p className={`${SECTION_LABEL} mb-2`}>Intake</p>
          <div className="space-y-1">
            {intakeResponses.map((r) => (
              <div key={r.questionSlug} className="flex items-start gap-2 text-sm">
                <span className="font-medium text-foreground capitalize">
                  {r.questionSlug.replace(/_/g, ' ')}
                </span>
                <span className="text-muted-foreground">{r.answer ? 'Yes' : 'No'}</span>
                {r.note && <span className="italic text-muted-foreground">{r.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Boarding-only: print run card + belongings */}
      {type === 'boarding' && (
        <div
          id={anchorId('belongings')}
          className={`space-y-3 border-t border-border px-5 py-3 ${anchorClass}`}
        >
          <button
            type="button"
            onClick={() => void openAndPrintRunCard(bookingId)}
            className={`${TOUCH} inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary`}
          >
            <Printer className="size-3.5" strokeWidth={1.75} />
            Print run card
          </button>
          <BelongingsSection bookingId={bookingId} />
        </div>
      )}

      {/* Structured feeding plan — stay override (migration 0101). */}
      {(type === 'boarding' || type === 'daycare') && (
        <div id={anchorId('feeding')} className={`border-t border-border px-5 py-3 ${anchorClass}`}>
          <BookingFeedingSection bookingId={bookingId} />
        </div>
      )}

      {/* Messages + live stay */}
      <div className="space-y-4 border-t border-border px-5 py-3">
        <ShareLiveStayPanel bookingId={bookingId} />
        {/* Pet report cards (migration 0093) — boarding + daycare only. */}
        <ReportCardsPanel bookingId={bookingId} bookingType={type} petName={pet.name} />
        <div id={anchorId('messages')} className={anchorClass}>
          <p className={`${SECTION_LABEL} mb-2`}>Messages</p>
          <BookingMessagesPanel
            bookingId={bookingId}
            realtimeNonce={messagesNonce}
            canModerate={canModerate}
          />
        </div>
      </div>

      {/* Guided check-in workflow drawer */}
      {checkinDrawerOpen && (
        <CheckinWorkflowDrawer
          bookingId={bookingId}
          entry={entry}
          customer={customer}
          currency={currency}
          onClose={() => onOpenWorkflow(null)}
          onCheckedIn={() => {
            onOpenWorkflow(null);
            onWorkflowDone();
          }}
        />
      )}

      {/* Guided checkout workflow drawer */}
      {checkoutDrawerOpen && (
        <CheckoutWorkflowDrawer
          bookingId={bookingId}
          entry={entry}
          currency={currency}
          totalMinor={totalMinor}
          onClose={() => onOpenWorkflow(null)}
          onCheckedOut={() => {
            onOpenWorkflow(null);
            onWorkflowDone();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BookingDetail({ groupId, groupDetail, onClose, onDeleted }: BookingDetailProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingStayId, setSavingStayId] = useState<string | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);

  // Cancel modal state
  const [cancelTarget, setCancelTarget] = useState<{ id: string; petName?: string } | null>(null);

  // Guided workflow drawer (check-in / checkout) — one open at a time across
  // the group. Owned here so both the per-pet buttons and the mobile bottom
  // bar drive the same drawer.
  const [workflow, setWorkflow] = useState<{ bookingId: string; kind: WorkflowKind } | null>(null);

  // Mobile chrome (section chips + bottom action bar) is only for the
  // full-page route; History embeds this component inside an expandable table
  // row where a fixed bar would float over unrelated rows.
  const mobileChrome = useLocation().pathname.startsWith('/bookings/');
  const [moreOpen, setMoreOpen] = useState(false);
  const [activeChip, setActiveChip] = useState<string | null>(null);
  // Outstanding balance reported by PaymentsPanel once the ledger has loaded.
  const [outstandingMinor, setOutstandingMinor] = useState<number | null>(null);

  // Notes / colour state (from enriched booking data).
  const [notes, setNotes] = useState('');
  const [colour, setColour] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const enrichedDetail = groupDetail as BookingGroupDetail;
  const rawEntries: unknown[] = enrichedDetail?.bookings ?? [];
  const entries: BookingGroupEntry[] = rawEntries.filter(isEnrichedEntry);
  const customer = (groupDetail as BookingGroupDetail)?.customer ?? null;

  // The first booking's facilityId is used to load the runs list.
  const facilityId = entries[0]?.booking.facilityId ?? null;

  // The first booking carries total + currency (from enriched shape).
  const firstBooking = entries[0]?.booking;
  const totalMinor = (firstBooking as unknown as Record<string, number>)?.totalMinor ?? 0;
  const currency: string =
    ((enrichedDetail as unknown as Record<string, unknown>)?.currency as string) ?? 'GBP';

  // Load notes / colour from the first booking (notes/colour are per-booking,
  // not on the group wrapper object).
  useEffect(() => {
    setNotes(entries[0]?.booking?.notes ?? '');
    setColour(entries[0]?.booking?.colour ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedDetail]);

  // Load runs for the facility.
  useEffect(() => {
    if (!facilityId) return;
    getRuns(facilityId)
      .then((rows) => setRuns(rows))
      .catch(() => setRuns([]));
  }, [facilityId]);

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (!groupDetail) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="mr-2 size-5 animate-spin" strokeWidth={1.75} />
        Loading booking…
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  function handleCancel(bookingId: string) {
    // Find the pet name for the modal title.
    const entry = entries.find((e) => e.booking.id === bookingId);
    const petName = entry?.pet?.name;
    const target: { id: string; petName?: string } = petName
      ? { id: bookingId, petName }
      : { id: bookingId };
    setCancelTarget(target);
  }

  async function handleConfirmCancel(reason: CancelReason, note: string) {
    if (!cancelTarget) return;
    const { id: bookingId } = cancelTarget;
    setCancelTarget(null);
    setBusy(`cancel-${bookingId}`);
    setError(null);
    try {
      const body = {
        reason: [reason, note].filter(Boolean).join(' — ') || reason,
      };
      await cancelBooking(bookingId, body);
      onClose();
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to cancel booking');
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this entire booking group? You can undo this for 30 days.')) return;
    setBusy('delete');
    setError(null);
    try {
      await deleteBookingGroup(groupId);
      // Soft delete — offer an undo that outlives this panel (onDeleted
      // usually navigates away). The banner lives in AppShell.
      pushUndo({
        message: 'Booking deleted',
        onUndo: async () => {
          const res = await restoreBookingGroup(groupId);
          return res.runClearedBookingIds.length > 0
            ? 'Booking restored — run was taken in the meantime, please re-assign'
            : 'Booking restored';
        },
      });
      onDeleted();
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to delete group');
    } finally {
      setBusy(null);
    }
  }

  async function handleSendToKiosk() {
    setBusy('kiosk');
    setError(null);
    try {
      await sendGroupToKiosk(groupId);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to send to kiosk');
    } finally {
      setBusy(null);
    }
  }

  async function handleAddToWaitlist() {
    const firstEntry = entries[0];
    if (!firstEntry || !customer || !facilityId) return;
    const stay = firstEntry.stay;
    if (!stay) return;
    if (!window.confirm('Add this booking to the waitlist for an earlier slot?')) return;
    setBusy('waitlist');
    setError(null);
    try {
      await createWaitlistEntry({
        facilityId,
        customerId: customer.id,
        petId: firstEntry.pet.id,
        requestedCheckIn: stay.checkIn,
        requestedCheckout: stay.checkout,
        notes: `Added from booking #${groupId.slice(-8).toUpperCase()}`,
      });
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to add to waitlist');
    } finally {
      setBusy(null);
    }
  }

  async function handleStaySave(
    bookingId: string,
    patch: {
      checkIn: string;
      checkout: string;
      feeding?: string | null;
      medication?: string | null;
    },
  ) {
    setSavingStayId(bookingId);
    setError(null);
    try {
      await patchBookingStayDates(bookingId, patch);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to save stay dates');
    } finally {
      setSavingStayId(null);
    }
  }

  async function handleRunChange(bookingId: string, runId: string | null) {
    try {
      await moveBoardingStayRun(bookingId, runId);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to reassign run');
    }
  }

  async function handleSaveMeta() {
    setSavingMeta(true);
    setError(null);
    try {
      // Patch all bookings in the group so notes/colour stay consistent.
      // patchBookingMetadata requires a booking ID, not the group ID.
      await Promise.all(
        entries.map((entry) =>
          patchBookingMetadata(entry.booking.id, { notes: notes || null, colour: colour || null }),
        ),
      );
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Failed to save notes');
    } finally {
      setSavingMeta(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const shortGroupId = groupId.slice(-8).toUpperCase();
  const createdAt = entries.length > 0 ? entries[0]?.booking.createdAt : null;
  const firstStatus = entries[0]?.booking.status ?? 'pending';
  const firstEntry = entries[0] ?? null;
  const firstType = firstEntry?.booking.type ?? null;

  // Section chips — only for sections that are actually rendered on this page
  // (ids live on the first pet card + the right-column panels).
  const sectionChips: SectionChip[] = [
    { id: 'section-details', label: 'Details' },
    ...(firstEntry ? [{ id: 'section-pets', label: 'Pets' }] : []),
    ...(firstType === 'boarding' && firstEntry?.stay
      ? [{ id: 'section-stay', label: 'Stay' }]
      : []),
    ...(firstType === 'boarding' || firstType === 'daycare'
      ? [{ id: 'section-feeding', label: 'Feeding' }]
      : []),
    ...(firstType === 'boarding' ? [{ id: 'section-belongings', label: 'Belongings' }] : []),
    ...(firstType === 'boarding' ? [{ id: 'section-transport', label: 'Transport' }] : []),
    ...(firstEntry ? [{ id: 'section-messages', label: 'Messages' }] : []),
    ...(firstEntry ? [{ id: 'section-payments', label: 'Payments' }] : []),
    ...(firstEntry ? [{ id: 'section-comms', label: 'Communications' }] : []),
    { id: 'section-activity', label: 'Activity' },
  ];

  // Primary next action for the bottom bar: Check in → Check out → Take
  // payment (outstanding > 0) → nothing. Multi-pet groups act on the first
  // pet that is still waiting; the in-card buttons cover the rest.
  const checkinEntry = entries.find((e) => canCheckinStatus(e.booking.status)) ?? null;
  const checkoutEntry = entries.find((e) => canCheckoutStatus(e.booking.status)) ?? null;
  const primaryAction: {
    label: string;
    icon: typeof LogIn;
    onClick: () => void;
    href?: string;
  } | null = checkinEntry
    ? {
        label: entries.length > 1 ? `Check in ${checkinEntry.pet.name}` : 'Check in',
        icon: LogIn,
        onClick: () => setWorkflow({ bookingId: checkinEntry.booking.id, kind: 'checkin' }),
      }
    : checkoutEntry
      ? {
          label: entries.length > 1 ? `Check out ${checkoutEntry.pet.name}` : 'Check out',
          icon: LogOut,
          onClick: () => setWorkflow({ bookingId: checkoutEntry.booking.id, kind: 'checkout' }),
        }
      : outstandingMinor !== null && outstandingMinor > 0
        ? {
            label: `Take payment · ${formatPriceMinor(outstandingMinor, currency)}`,
            icon: CreditCard,
            href: '#section-payments',
            onClick: () => {
              setActiveChip('section-payments');
              scrollToSection('section-payments');
            },
          }
        : null;

  const canAddToWaitlist = Boolean(firstEntry?.stay && customer && facilityId);
  const firstBoardingEntry = entries.find((e) => e.booking.type === 'boarding') ?? null;
  const firstPet = firstEntry?.pet ?? null;

  const secondaryButton =
    'flex w-full min-h-11 items-center gap-2.5 rounded-lg border border-border bg-surface px-4 text-sm font-medium hover:bg-secondary disabled:opacity-60 press';

  return (
    <div className={`space-y-6 ${mobileChrome ? 'pb-24 md:pb-0' : ''}`}>
      {/* Mobile section chips — sticky under the AppShell mobile header. */}
      {mobileChrome && (
        <nav
          aria-label="Booking sections"
          data-testid="booking-section-chips"
          className={`sticky ${MOBILE_CHIP_TOP} z-10 -mx-4 border-b border-border bg-background/95 backdrop-blur-sm sm:-mx-6 md:hidden`}
        >
          <div className="flex gap-2 overflow-x-auto px-4 py-1 sm:px-6">
            {sectionChips.map((chip) => {
              const active = activeChip === chip.id;
              return (
                <a
                  key={chip.id}
                  href={`#${chip.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveChip(chip.id);
                    scrollToSection(chip.id);
                  }}
                  className={`inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full border px-3.5 text-xs font-medium press ${
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-surface text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {chip.label}
                </a>
              );
            })}
          </div>
        </nav>
      )}

      {/* Cancel booking modal */}
      <CancelBookingModal
        open={cancelTarget !== null}
        petName={cancelTarget?.petName}
        onConfirm={handleConfirmCancel}
        onCancel={() => setCancelTarget(null)}
      />

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_480px]">
        {/* ===== LEFT COLUMN ===== */}
        <div className="space-y-6">
          {/* Booking Details card */}
          <div id="section-details" className={`${CARD} ${SECTION_ANCHOR}`}>
            <div className={SECTION_HEADER}>
              <FileText className="size-4 text-muted-foreground" strokeWidth={1.75} />
              <h3 className="font-heading text-sm font-semibold text-foreground">
                Booking Details
              </h3>
            </div>
            <div className="px-5 py-4 space-y-4">
              {/* Ref + status row */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className={SECTION_LABEL}>Reference</p>
                  <p className="num mt-0.5 font-mono text-sm font-semibold text-foreground">
                    #{shortGroupId}
                  </p>
                </div>
                <StatusPill status={firstStatus} />
              </div>

              {/* Customer */}
              <div>
                <p className={`${SECTION_LABEL} flex items-center gap-1.5`}>
                  <User className="size-3.5" strokeWidth={1.75} />
                  Customer
                </p>
                {customer ? (
                  <div className="mt-0.5">
                    <Link
                      to={`/customers/${customer.id}`}
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      {customer.firstName} {customer.lastName}
                    </Link>
                    {customer.phone && (
                      <p className="num text-xs text-muted-foreground">{customer.phone}</p>
                    )}
                    {customer.email && (
                      <p className="text-xs text-muted-foreground">{customer.email}</p>
                    )}
                  </div>
                ) : (
                  <p className="mt-0.5 text-sm text-muted-foreground">Unknown customer</p>
                )}
              </div>

              {/* Created timestamp */}
              {createdAt && (
                <div>
                  <p className={`${SECTION_LABEL} flex items-center gap-1.5`}>
                    <Clock className="size-3.5" strokeWidth={1.75} />
                    Created
                  </p>
                  <p className="num mt-0.5 text-sm text-foreground">{formatDateTime(createdAt)}</p>
                </div>
              )}

              {/* Booking notes (editable) */}
              <EditableTextarea
                label="Booking Notes"
                value={notes}
                placeholder="Add notes about this booking…"
                onSave={(v) => setNotes(v)}
                draftKey={`booking-notes:${groupId}`}
              />

              {/* Save notes button — only visible when notes has been edited via the textarea */}
              <button
                type="button"
                disabled={savingMeta}
                onClick={() => void handleSaveMeta()}
                className={`${TOUCH} inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60`}
              >
                {savingMeta ? (
                  <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                ) : null}
                Save notes
              </button>
            </div>
          </div>

          {/* Per-pet sections */}
          {entries.length === 0 ? (
            <div className={`${CARD} px-5 py-8 text-center`}>
              <CheckCircle
                className="mx-auto mb-2 size-6 text-muted-foreground"
                strokeWidth={1.75}
              />
              <p className="text-sm text-muted-foreground">
                Booking details are loading or unavailable.
              </p>
            </div>
          ) : (
            entries.map((entry, index) => (
              <PetSection
                key={entry.booking.id}
                entry={entry}
                runs={runs}
                savingStayId={savingStayId}
                busy={busy}
                customer={customer}
                currency={currency}
                totalMinor={totalMinor}
                onCancel={(id) => void handleCancel(id)}
                onWorkflowDone={onClose}
                onStaySave={handleStaySave}
                onRunChange={handleRunChange}
                openWorkflow={workflow?.bookingId === entry.booking.id ? workflow.kind : null}
                onOpenWorkflow={(kind) =>
                  setWorkflow(kind ? { bookingId: entry.booking.id, kind } : null)
                }
                anchors={index === 0}
              />
            ))
          )}

          {/* Transport legs — group-level (attached to the primary booking). */}
          {firstType === 'boarding' && entries.length > 0 && (
            <BookingTransportSection
              id="section-transport"
              className={SECTION_ANCHOR}
              bookingIds={entries.map((e) => e.booking.id)}
              currency={currency}
            />
          )}
        </div>

        {/* ===== RIGHT COLUMN ===== */}
        <div className="space-y-6">
          {/* Group-level actions — on the full-page mobile layout these live
              behind the bottom bar's "More" sheet instead. */}
          <div className={`${CARD} ${mobileChrome ? 'hidden md:block' : ''}`}>
            <div className="flex flex-wrap gap-2 px-5 py-3">
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void handleSendToKiosk()}
                className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-60"
              >
                {busy === 'kiosk' ? (
                  <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Send className="size-3.5" strokeWidth={1.75} />
                )}
                Send to kiosk
              </button>
              {entries[0]?.stay && customer && facilityId && (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => void handleAddToWaitlist()}
                  className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-60"
                  title="Add to waitlist for an earlier slot"
                >
                  {busy === 'waitlist' ? (
                    <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                  ) : (
                    <ListPlus className="size-3.5" strokeWidth={1.75} />
                  )}
                  Add to waitlist
                </button>
              )}
              <button
                type="button"
                disabled={!!busy}
                onClick={() => void handleDelete()}
                className="flex items-center gap-1.5 rounded-md border border-destructive/30 bg-background px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-60"
              >
                {busy === 'delete' ? (
                  <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                ) : (
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                )}
                Delete group
              </button>
            </div>
          </div>

          {/* Payments panel */}
          {entries.length > 0 && entries[0] && (
            <PaymentsPanel
              bookingId={entries[0].booking.id}
              totalMinor={totalMinor}
              currency={currency}
              customer={customer}
              onOutstandingChange={setOutstandingMinor}
            />
          )}

          {/* Communications panel */}
          {entries.length > 0 && entries[0] && <CommsPanel bookingId={entries[0].booking.id} />}

          {/* Activity panel */}
          <ActivityPanel bookingId={groupId} />
        </div>
      </div>

      {/* Mobile bottom action bar — primary next action + "More". */}
      {mobileChrome && (
        <div
          data-testid="booking-mobile-bar"
          className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur-sm md:hidden"
        >
          <div className="flex items-stretch gap-2">
            {primaryAction ? (
              primaryAction.href ? (
                <a
                  href={primaryAction.href}
                  onClick={(e) => {
                    e.preventDefault();
                    primaryAction.onClick();
                  }}
                  data-testid="booking-mobile-primary"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90 press"
                >
                  <primaryAction.icon className="size-4" strokeWidth={1.75} />
                  <span className="num truncate">{primaryAction.label}</span>
                </a>
              ) : (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={primaryAction.onClick}
                  data-testid="booking-mobile-primary"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60 press"
                >
                  <primaryAction.icon className="size-4" strokeWidth={1.75} />
                  <span className="truncate">{primaryAction.label}</span>
                </button>
              )
            ) : (
              <div className="flex min-h-11 min-w-0 flex-1 items-center gap-2 px-1 text-xs text-muted-foreground">
                <StatusPill status={firstStatus} />
                <span className="truncate">No further action</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label="More actions"
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-medium hover:bg-secondary press"
            >
              <MoreHorizontal className="size-4" strokeWidth={1.75} />
              More
            </button>
          </div>
        </div>
      )}

      {/* "More" sheet — the group-level actions, reusing the same handlers. */}
      {mobileChrome && moreOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="More actions"
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMoreOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border-t border-border bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-heading text-sm font-semibold text-foreground">More actions</p>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground"
                aria-label="Close more actions"
              >
                <XCircle className="size-5" strokeWidth={1.75} />
              </button>
            </div>
            <div className="grid gap-2">
              <button
                type="button"
                disabled={!!busy}
                onClick={() => {
                  setMoreOpen(false);
                  void handleSendToKiosk();
                }}
                className={secondaryButton}
              >
                <Send className="size-4 text-muted-foreground" strokeWidth={1.75} />
                Send to kiosk
              </button>
              {canAddToWaitlist && (
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={() => {
                    setMoreOpen(false);
                    void handleAddToWaitlist();
                  }}
                  className={secondaryButton}
                >
                  <ListPlus className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  Add to waitlist
                </button>
              )}
              {firstBoardingEntry && (
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    void openAndPrintRunCard(firstBoardingEntry.booking.id);
                  }}
                  className={secondaryButton}
                >
                  <Printer className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  Print run card
                </button>
              )}
              {firstPet?.id && firstEntry && (
                <Link
                  to={`/incidents/new?petId=${firstPet.id}&petName=${encodeURIComponent(firstPet.name)}&bookingId=${firstEntry.booking.id}`}
                  onClick={() => setMoreOpen(false)}
                  className={secondaryButton}
                >
                  <AlertTriangle className="size-4 text-muted-foreground" strokeWidth={1.75} />
                  Report incident
                </Link>
              )}
              <button
                type="button"
                disabled={!!busy}
                onClick={() => {
                  setMoreOpen(false);
                  void handleDelete();
                }}
                className={`${secondaryButton} border-destructive/30 text-destructive hover:bg-destructive/10`}
              >
                <Trash2 className="size-4" strokeWidth={1.75} />
                Delete group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
